import { Button, Form, IFormRender } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import { ClassName } from "types/ClassName";
import Typography from 'components/foundation/text/Typography/Typography';
import HeaderText from 'containers/settings/components/HeaderText/HeaderText';
import { useToastOpen } from 'contexts/ToastContext';
import { usePositionUpdate } from 'hooks';
import useClockInRestrictionTimeConverter from 'hooks/models/position/useClockInRestrictionTimeConverter';
import isEqual from 'lodash/isEqual';
import { DateTime } from 'luxon';
import { t } from 'utils/localize';
import { ClockInRestrictionType } from '__generated__/graphql';
import ClockInRestrictionFormFields from '../components/ClockInRestrictionFormFields/ClockInRestrictionFormFields';

interface IClockInRestrictionFormData {
  clockInRestriction: ClockInRestrictionType;
  clockInRestrictionStartTime: DateTime | null;
}

export interface IClockInRestrictionFormProps {
  positionId: string;
  onSubmit: (formData: {
    clockInRestriction: ClockInRestrictionType;
    clockInRestrictionStartTime: string | undefined;
  }) => void;
  currentSetting: IClockInRestrictionFormData;
  className?: ClassName;
}

function ClockInRestrictionForm({
  positionId,
  onSubmit,
  currentSetting: initialFormData,
  className,
}: IClockInRestrictionFormProps) {
  const [, update] = usePositionUpdate();
  const openToast = useToastOpen();
  const classes = classNames('clock-in-restriction-form', className);
  const { toClockInRestriction } = useClockInRestrictionTimeConverter();

  async function onFormSubmit(formData: IClockInRestrictionFormData) {
    const updatedValue = {
      clockInRestriction: formData.clockInRestriction,
      clockInRestrictionStartTime: toClockInRestriction(formData.clockInRestrictionStartTime),
    };

    if (!isEqual(initialFormData, updatedValue)) {
      try {
        await update({ ...updatedValue, id: positionId });
      } catch (e) {
        openToast({ label: t('Something went wrong. Please try again later.') });
      }
    }

    onSubmit(updatedValue);
  }

  function renderFormFields(form: IFormRender<IClockInRestrictionFormData>) {
    return (
      <>
        <ClockInRestrictionFormFields form={form} />
        <Button type="primary" onClick={form.handleSubmit}>
          {t('Update')}
        </Button>
      </>
    );
  }

  return (
    <div className={classes}>
      <HeaderText title={t('Clock-In Restrictions')} />
      <Typography tag="span" className="mb-4">
        {t('Prevent employees from clocking in before a specified time, or without a scheduled work item.')}
      </Typography>
      <Form render={renderFormFields} onSubmit={onFormSubmit} data={initialFormData} />
    </div>
  );
}

export default ClockInRestrictionForm;
